/**
 * escalationSignalsSelector — pure read-only derivation
 * ─────────────────────────────────────────────────────
 * Clusters operational stress across in-flight installs into a small set of
 * "escalation signals" the operator can scan in seconds. Pure function over
 * canonical inputs (InstallsLiveRows + JobRiskSignals); no writes, no new
 * sinks, no telemetry.
 *
 * Hard rules:
 *   1. Read-only. No mutations, no side effects.
 *   2. No `contractValue` or `payoutAmount` references anywhere.
 *   3. Sample threshold: only emit a signal when the cluster has at least
 *      MIN_SAMPLE_FOR_DISPLAY rows (matches the platform analytics rule).
 *   4. Output is fully deterministic given inputs (sorted by severity desc,
 *      then size desc, then key asc).
 */
import { MIN_SAMPLE_FOR_DISPLAY } from "@/lib/analyticsThresholds";
import type { InstallsLiveRow } from "./installsLiveSelector";
import type { JobRiskSignals } from "../scheduling/riskSignals";

export type EscalationKind = "installer_overload" | "qa_backlog" | "stalled_cluster" | "risk_concentration";

export type EscalationSeverity = "low" | "medium" | "high";

export interface EscalationSignal {
  /** Stable id derived from (kind, key) — usable as React key. */
  id: string;
  kind: EscalationKind;
  /** What the cluster is grouped by (installer name, "global", etc.). */
  groupKey: string;
  /** Short headline copy, plain language. */
  headline: string;
  /** Supporting one-liner. */
  detail: string;
  severity: EscalationSeverity;
  /** Job ids in the cluster (for downstream drill-in / quick actions). */
  jobIds: readonly string[];
}

export interface EscalationSignalsInput {
  rows: readonly InstallsLiveRow[];
  /** Job-id → risk signals (from getJobRiskSignals). */
  riskByJob: ReadonlyMap<string, JobRiskSignals>;
}

const SEVERITY_RANK: Record<EscalationSeverity, number> = { high: 3, medium: 2, low: 1 };

function severityForCount(n: number, mediumAt = 4, highAt = 6): EscalationSeverity {
  if (n >= highAt) return "high";
  if (n >= mediumAt) return "medium";
  return "low";
}

export function buildEscalationSignals(input: EscalationSignalsInput): EscalationSignal[] {
  const out: EscalationSignal[] = [];

  // 1) Installer overload — same installer carrying ≥3 in-flight installs today.
  const byInstaller = new Map<string, InstallsLiveRow[]>();
  for (const r of input.rows) {
    if (!r.installerName || r.installerName === "Unassigned") continue;
    const arr = byInstaller.get(r.installerName) ?? [];
    arr.push(r);
    byInstaller.set(r.installerName, arr);
  }
  for (const [installer, rows] of byInstaller) {
    if (rows.length < MIN_SAMPLE_FOR_DISPLAY) continue;
    const stalled = rows.filter((r) => r.isStale).length;
    out.push({
      id: `installer_overload:${installer}`,
      kind: "installer_overload",
      groupKey: installer,
      headline: `${installer} is on ${rows.length} active installs`,
      detail: stalled > 0
        ? `${stalled} stalled · check capacity before adding more.`
        : "Heavy load — review before assigning new work today.",
      severity: severityForCount(rows.length, 4, 6),
      jobIds: rows.map((r) => r.jobId),
    });
  }

  // 2) QA backlog — system-wide rows awaiting QA review.
  const qaRows = input.rows.filter((r) => r.awaitingQa);
  if (qaRows.length >= MIN_SAMPLE_FOR_DISPLAY) {
    out.push({
      id: "qa_backlog:global",
      kind: "qa_backlog",
      groupKey: "global",
      headline: `${qaRows.length} installs awaiting QA review`,
      detail: "Review before installer leaves site to avoid callbacks.",
      severity: severityForCount(qaRows.length, 5, 8),
      jobIds: qaRows.map((r) => r.jobId),
    });
  }

  // 3) Stalled cluster — system-wide stale rows.
  const staleRows = input.rows.filter((r) => r.isStale);
  if (staleRows.length >= MIN_SAMPLE_FOR_DISPLAY) {
    out.push({
      id: "stalled_cluster:global",
      kind: "stalled_cluster",
      groupKey: "global",
      headline: `${staleRows.length} installs have stalled`,
      detail: "No phase activity beyond the stale threshold — confirm crews are still on-site.",
      severity: severityForCount(staleRows.length, 4, 7),
      jobIds: staleRows.map((r) => r.jobId),
    });
  }

  // 4) Risk concentration — installers carrying multiple risky jobs (per
  // canonical risk signals: reschedules / extensions / conflicts).
  const riskByInstaller = new Map<string, InstallsLiveRow[]>();
  for (const r of input.rows) {
    if (!r.installerName || r.installerName === "Unassigned") continue;
    const sig = input.riskByJob.get(r.jobId);
    if (!sig?.hasAny) continue;
    const arr = riskByInstaller.get(r.installerName) ?? [];
    arr.push(r);
    riskByInstaller.set(r.installerName, arr);
  }
  for (const [installer, rows] of riskByInstaller) {
    if (rows.length < MIN_SAMPLE_FOR_DISPLAY) continue;
    out.push({
      id: `risk_concentration:${installer}`,
      kind: "risk_concentration",
      groupKey: installer,
      headline: `${installer} holds ${rows.length} risky installs`,
      detail: "Reschedules, conflicts, or prior extensions concentrated on one crew.",
      severity: severityForCount(rows.length, 3, 5),
      jobIds: rows.map((r) => r.jobId),
    });
  }

  return out.sort((a, b) => {
    const s = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (s !== 0) return s;
    const n = b.jobIds.length - a.jobIds.length;
    if (n !== 0) return n;
    return a.id.localeCompare(b.id);
  });
}
